import { api } from './client';

/** Fixed, known field shapes — `legalEntities`/`warehouses` select these explicitly. */
export interface CompanyRow {
  LegalEntityId: string;
  Name: string;
}

export interface WarehouseRow {
  WarehouseId: string;
  WarehouseName: string;
}

/**
 * Financial-dimension row shape is unverified against the client's D365
 * configuration pending #21 — kept generic rather than guessing field names.
 */
export type DimensionRow = Record<string, unknown>;

/**
 * Vendors are the one master-data list the API reshapes rather than proxying
 * verbatim, so these are the API's own names — not D365's. Deliberate: two of
 * the underlying `VendorsV2` property names were wrong and had to change.
 */
export interface VendorRow {
  accountNumber: string;
  name: string;
  paymentTerms: string;
  currencyCode: string;
  city: string;
}

export interface UnitRow {
  UnitSymbol: string;
  UnitDescription: string;
}

export interface ProjectRow {
  ProjectID: string;
  ProjectName: string;
}

/** Confirmed live against `ProcurementProductCategories` — there is no `CategoryId`/`Id`/`Value`
 * field on this entity at all, only `CategoryCode` (the stable identifier) and `CategoryName`. */
export interface ProcurementCategoryRow {
  CategoryCode: string;
  CategoryName: string;
}

export const getCompanies = (): Promise<CompanyRow[]> =>
  api<CompanyRow[]>('/api/masterdata/companies');

export const getCostCentres = (company: string): Promise<DimensionRow[]> =>
  api<DimensionRow[]>(`/api/masterdata/${encodeURIComponent(company)}/cost-centres`);

export const getLineOfBusiness = (company: string): Promise<DimensionRow[]> =>
  api<DimensionRow[]>(`/api/masterdata/${encodeURIComponent(company)}/line-of-business`);

export const getWarehouses = (company: string): Promise<WarehouseRow[]> =>
  api<WarehouseRow[]>(`/api/masterdata/${encodeURIComponent(company)}/warehouses`);

/** A D365 sales tax group, as the line-item dropdown offers it (the `code` is what a line stores). */
export interface SalesTaxGroupRow {
  code: string;
  description: string;
}

/** Live from D365, per company — never persisted here. */
export const getSalesTaxGroups = (company: string): Promise<SalesTaxGroupRow[]> =>
  api<SalesTaxGroupRow[]>(`/api/masterdata/${encodeURIComponent(company)}/sales-tax-groups`);

/** A D365 project category, as the reviewer's dropdown offers it (the `code` is what a request stores). */
export interface ProjectCategoryRow {
  code: string;
  description: string;
}

/** Live from D365, per company — never persisted here. */
export const getProjectCategories = (company: string): Promise<ProjectCategoryRow[]> =>
  api<ProjectCategoryRow[]>(`/api/masterdata/${encodeURIComponent(company)}/project-categories`);

/** A D365 main account, as the Main account dropdown offers it (the `code` is what a request stores). */
export interface MainAccountRow {
  code: string;
  description: string;
}

/**
 * Live from D365 — never persisted here. Main accounts belong to the chart of accounts, not to a
 * company, so the list is the same for every company; the parameter only lets the field reuse the
 * company-gated loader every other header dropdown uses.
 */
export const getMainAccounts = (_company?: string): Promise<MainAccountRow[]> =>
  api<MainAccountRow[]>('/api/masterdata/main-accounts');

/** `company=` scopes a per-company list; omitted, D365 falls back to the service account's default company. */
const companyParam = (company: string | null | undefined): string =>
  company ? `&company=${encodeURIComponent(company)}` : '';

/**
 * Server-side search — `query` must be at least 2 characters (enforced by the API too).
 * Vendors are per-company in F&O, so pass the request's company whenever one is chosen.
 */
export const getVendors = (
  query: string,
  page = 1,
  company?: string | null,
): Promise<VendorRow[]> =>
  api<VendorRow[]>(
    `/api/masterdata/vendors?q=${encodeURIComponent(query)}&page=${String(page)}${companyParam(company)}`,
  );

/**
 * ONE vendor by exact account, in one company — the cheap lookup for a screen
 * that already holds an account number and only needs the name (lists). 404 when
 * that company has no such vendor. Prefer this over `getVendors`, whose search
 * makes the API read a window of hundreds of vendors from D365 per call.
 */
export const getVendor = (account: string, company: string): Promise<VendorRow> =>
  api<VendorRow>(
    `/api/masterdata/vendors/${encodeURIComponent(account)}?company=${encodeURIComponent(company)}`,
  );

/** Shared by every line — fetch once per form load, not per row (#26). */
export const getProcurementCategories = (): Promise<ProcurementCategoryRow[]> =>
  api<ProcurementCategoryRow[]>('/api/masterdata/procurement-categories');

/** Shared by every line — fetch once per form load, not per row (#26). */
export const getUnits = (): Promise<UnitRow[]> => api<UnitRow[]>('/api/masterdata/units');

/** Only needed once the purchase type is Capital, where a project is mandatory. Per-company in F&O. */
export const getProjects = (company?: string | null): Promise<ProjectRow[]> =>
  api<ProjectRow[]>(
    company
      ? `/api/masterdata/projects?company=${encodeURIComponent(company)}`
      : '/api/masterdata/projects',
  );
